import type { I18NReport, RecordDetailsDtoTranslationUnitDto } from '@/api/generated/rest-v4-api';
import { captionText } from '@/utils/caption';

export type RecordStatus = 'overdue' | 'progress' | 'completed' | 'future';

export interface ScheduleRecord {
  id: string;
  date: string;
  status: RecordStatus;
  label: string;
  docNumber?: number;
  link: string;
}

export interface ScheduleRow {
  id: number;
  description: string;
  form: string;
  schedule: string;
  hierarchy: Record<string, string>;
  records: ScheduleRecord[];
}

const COMPLETED_STATUSES = new Set([
  'Complete',
  'Completed',
  'Resolved',
  'Archived',
  'Closed',
  'Completed (Test form)',
]);

// Statuses are free text per workflow; the grid and the stat cards share this one reading of them.
export const classifyStatus = (label: string): Exclude<RecordStatus, 'future'> => {
  if (label.toLowerCase() === 'overdue') {
    return 'overdue';
  }
  return COMPLETED_STATUSES.has(label) ? 'completed' : 'progress';
};

const isFuture = (record: RecordDetailsDtoTranslationUnitDto, containsHistoricalRecords: boolean, now: Date) =>
  containsHistoricalRecords ? Boolean(record.projected) : !(new Date(record.createdAt ?? '') < now);

const scheduleInterval = (rules: NonNullable<I18NReport['schedule']>['rules']) => {
  const rule = rules?.[0];
  return rule && 'interval' in rule && rule.interval ? String(rule.interval) : '';
};

const sanitizeRecords = (
  records: RecordDetailsDtoTranslationUnitDto[],
  scheduleId: number,
  containsHistoricalRecords: boolean,
  now: Date
): ScheduleRecord[] =>
  records
    ? records.flatMap((record, index) => {
        if (!record.createdAt) {
          return [];
        }
        const isPlanned = isFuture(record, containsHistoricalRecords, now);
        const label = isPlanned ? '' : captionText(record.status);
        return [
          {
            id: `${scheduleId}-${index}`,
            date: record.createdAt.slice(0, 10),
            status: isPlanned ? 'future' : classifyStatus(label),
            label,
            docNumber: record.docNumber,
            link: record.linkUrl ?? '',
          },
        ];
      })
    : [];

// A record's calendar date is the one the server wrote; it is compared as a string, never re-parsed into local time.
export const toScheduleRows = (reports: I18NReport[], now: Date): ScheduleRow[] =>
  reports.flatMap(({ schedule, records = [], containsHistoricalRecords = false }) => {
    if (schedule?.id === undefined) {
      return [];
    }
    const scheduleId = schedule.id;

    return [
      {
        id: scheduleId,
        description: captionText(schedule.caption) || '-',
        form: captionText(schedule.moduleFormCaption),
        schedule: scheduleInterval(schedule.rules),
        hierarchy: Object.fromEntries(
          (schedule.hierarchyFields ?? []).flatMap((field) =>
            field.hierarchyTypeId === undefined ? [] : [[String(field.hierarchyTypeId), captionText(field.caption)]]
          )
        ),
        records: sanitizeRecords(records, scheduleId, containsHistoricalRecords, now),
      },
    ];
  });
